import axios from 'axios';
import {
  buildPasteBodyFromAxios,
  buildPasteBodyFromVueError,
  buildPasteBodyFromRejection,
  buildPasteBodyFromWindowError,
  buildPasteBodyFromRouterError,
  axiosEntrySummary,
} from '@/utils/errorNotebookCapture';

const STORAGE_KEY = 'kosexa_tienda_error_notebook_v1';
const MAX_ENTRIES = 50;

function loadPersisted() {
  if (typeof sessionStorage === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed.entries) ? parsed.entries : [];
  } catch {
    return [];
  }
}

function persist(entries) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ entries }));
  } catch {
    /* quota / privado */
  }
}

function newId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default {
  namespaced: true,
  state: () => ({
    entries: loadPersisted(),
    testSessionActive: false,
  }),
  getters: {
    entries: (s) => s.entries,
    count: (s) => s.entries.length,
    testSessionActive: (s) => s.testSessionActive,
  },
  mutations: {
    push(state, entry) {
      state.entries.unshift(entry);
      if (state.entries.length > MAX_ENTRIES) {
        state.entries.length = MAX_ENTRIES;
      }
      persist(state.entries);
    },
    clear(state) {
      state.entries = [];
      persist(state.entries);
    },
    setTestSession(state, v) {
      state.testSessionActive = Boolean(v);
    },
    remove(state, id) {
      state.entries = state.entries.filter((e) => e.id !== id);
      persist(state.entries);
    },
  },
  actions: {
    pushAxiosError({ commit }, { error, route }) {
      if (!error) return;
      if (axios.isCancel?.(error)) return;
      if (error.code === 'ERR_CANCELED') return;

      const body = buildPasteBodyFromAxios(error, route);
      commit('push', {
        id: newId(),
        ts: new Date().toISOString(),
        kind: 'http',
        summary: axiosEntrySummary(error),
        body,
      });
    },
    pushVueError({ commit }, { err, info, route }) {
      commit('push', {
        id: newId(),
        ts: new Date().toISOString(),
        kind: 'vue',
        summary: `${err?.name || 'Error'}: ${(err?.message || '').slice(0, 120)}`,
        body: buildPasteBodyFromVueError(err, info, route),
      });
    },
    pushRejection({ commit }, { reason, route }) {
      commit('push', {
        id: newId(),
        ts: new Date().toISOString(),
        kind: 'rejection',
        summary:
          reason instanceof Error
            ? `${reason.name}: ${reason.message.slice(0, 100)}`
            : 'Promesa rechazada (ver cuerpo)',
        body: buildPasteBodyFromRejection(reason, route),
      });
    },
    pushWindowError({ commit }, { ev, route }) {
      const msg = ev?.message || 'Error en ventana';
      commit('push', {
        id: newId(),
        ts: new Date().toISOString(),
        kind: 'window',
        summary: String(msg).slice(0, 120),
        body: buildPasteBodyFromWindowError(ev, route),
      });
    },
    pushRouterError({ commit }, { err, route }) {
      commit('push', {
        id: newId(),
        ts: new Date().toISOString(),
        kind: 'router',
        summary: (err?.message || 'Error de ruta').slice(0, 120),
        body: buildPasteBodyFromRouterError(err, route),
      });
    },
    startTestSession({ commit }) {
      commit('clear');
      commit('setTestSession', true);
      commit('push', {
        id: newId(),
        ts: new Date().toISOString(),
        kind: 'manual',
        summary: 'Sesión de prueba iniciada',
        body: [
          '===== KOSEXA Tienda · cuaderno de errores =====',
          `Fecha (ISO): ${new Date().toISOString()}`,
          '',
          'Cuaderno vaciado. Navegue y reproduzca el problema; use «Copiar para agente» para Markdown con todo el contexto.',
        ].join('\n'),
      });
    },
    pushManual({ commit }, { summary, detail, route }) {
      const bodyLines = [
        '===== KOSEXA Tienda · cuaderno de errores (manual) =====',
        `Fecha (ISO): ${new Date().toISOString()}`,
        '',
        String(detail || '').slice(0, 12000),
      ];
      if (route) {
        bodyLines.push('', '--- Ruta al registrar ---', String(route.fullPath || route.path || '—'));
      }
      commit('push', {
        id: newId(),
        ts: new Date().toISOString(),
        kind: 'manual',
        summary: String(summary || 'Nota manual').slice(0, 160),
        body: bodyLines.join('\n'),
      });
    },
  },
};
